// Feature flags — single source of truth for temporarily hidden features.
// Flip a flag here and every UI entry point (menus, tiles, sub-links,
// showcase strips, etc.) hides in one step. Keeping the routes reachable
// via URL is intentional for QA + admin poke-testing.

const DEFAULTS = {
  // "Ask the Globe" — kid-safe AI Q&A. Backing endpoint /api/ask is
  // online and answers via the Emergent LLM key. Feb 2026: re-enabled
  // after the double-/api prefix bug fix in AskTheGlobe.jsx.
  ask: true,
  // Compass — the "whole-world tutor" (Socratic answer + lesson-card
  // citation). Backing endpoint /api/search is online. Feb 2026:
  // re-enabled per user request. Home tiles, marketing copy and the
  // /compass route are all live again.
  compass: true,
  // TTQS — Times Tables Quest. Kickoff phase (Teach / Fluency Sprint /
  // Fact Repair, Y1–10) behind a flag: hidden from menus by default, but
  // the /times-tables-quest route stays URL-reachable for QA.
  ttqs: true,
  // Core Product Experience 2.0 (Jun 2026). Additive UI layers over the
  // protected routes; every flag OFF must leave the original page intact.
  // Runtime override: localStorage mwq_flags_v1 = {"passport2":false} — or
  // ?flag_passport2=0 once (persisted). No flag ever touches learner data.
  passport2: true,
  nextAdventure: true,
  games2: true,
  globeAdventure: true,
  family2: true,
  home2: true,
  rewardExplain: true,
  // Jun 2026 · rare positive surprises (1 in 12 meaningful completions). Experiment: disable via ?flag_surprise=0.
  surprise: true,
  // Oct 2026 — guided mission player pilot (Year 8 PSHE). false → classic UnitPage only.
  missions: true,
};

export const CORE_EXPERIENCE_FLAGS = ["passport2", "nextAdventure", "games2", "globeAdventure", "family2", "home2", "rewardExplain"];
const LOCAL_KEY = "mwq_flags_v1";

function _localFlags() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "{}") || {}; } catch { return {}; }
}

export function setFlag(name, value) {
  const next = { ..._localFlags() };
  if (value === null || value === undefined) delete next[name]; else next[name] = !!value;
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(next)); window.dispatchEvent(new CustomEvent("mwq-flags-changed")); } catch { /* ignore */ }
}

// One-shot URL overrides: ?flag_passport2=0 → persisted to localStorage.
(function _absorbUrlFlags() {
  try {
    const qs = new URLSearchParams(window.location.search);
    for (const [k, v] of qs.entries()) {
      if (k.startsWith("flag_")) setFlag(k.slice(5), v === "1" || v === "true");
    }
  } catch { /* SSR / no window */ }
})();

export function flag(name) {
  const local = _localFlags();
  if (typeof local[name] === "boolean") return local[name];
  return isFeatureEnabled(name);
}

export function allFlags() {
  return Object.fromEntries(Object.keys(DEFAULTS).map((k) => [k, flag(k)]));
}

function _envFlag(key) {
  const cra = process.env[`REACT_APP_FLAG_${String(key).toUpperCase()}`];
  if (cra === "1" || cra === "true") return true;
  if (cra === "0" || cra === "false") return false;
  return null;
}

export function isFeatureEnabled(name) {
  const fromEnv = _envFlag(name);
  if (fromEnv !== null) return fromEnv;
  return !!DEFAULTS[name];
}

// Legacy shape — many components still `import { FEATURES } from …`.
// We keep it exported so no consumer has to migrate at once.
export const FEATURES = new Proxy(DEFAULTS, {
  get(target, prop) {
    if (typeof prop !== "string") return undefined;
    const fromEnv = _envFlag(prop);
    if (fromEnv !== null) return fromEnv;
    return target[prop];
  },
});

// Named convenience so imports read like `if (compassEnabled) …`.
export const compassEnabled = isFeatureEnabled("compass");
